import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Image
} from "react-native";
import { useTranslation } from 'react-i18next';
import IdCard from "../../../../../../../../assets/images/id-card.png";
// import { Ionicons } from "@expo/vector-icons"; // or lucide-react if you prefer
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export default function EnterSenderNameStep({ onValidationChange }: Props) {
  const { t } = useTranslation();

  // display the field label from the gffpFieldsByCategory (from gffp response)
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const senderInfoFields: any[] = (gffpFieldsByCategory?.["Sender Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = senderInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    return field?.FieldLabel ?? field?.fieldLabel;
  };

  const firstNamePlaceholder = getFieldLabel("senderFirstName") || t("FIRST_NAME");
  const middleNamePlaceholder = getFieldLabel("senderMiddleName") || t("MIDDLE_NAME");
  const lastNamePlaceholder = getFieldLabel("senderLastName") || t("LAST_NAME");
  const initialFirst = useSessionStore((s: SessionState) => (s as any).senderFirstName) || "";
  const initialMiddle = useSessionStore((s: SessionState) => (s as any).senderMiddleName) || "";
  const initialLast = useSessionStore((s: SessionState) => (s as any).senderLastName) || "";
  const [firstName, setFirstName] = useState(initialFirst);
  const [middleName, setMiddleName] = useState(initialMiddle);
  const [lastName, setLastName] = useState(initialLast);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <Text style={styles.title}>{t("SENDER_NAME_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_NAME_SUBTITLE")}</Text>

      {/* First name */}
      <TextInput
        style={styles.input}
        placeholder={firstNamePlaceholder}
        value={firstName}
        onChangeText={(t) => { setFirstName(t); try { setSenderFirstName?.(t); } catch {} }}
      />

      {/* Middle name */}
      <TextInput
        style={styles.input}
        placeholder={middleNamePlaceholder}
        value={middleName}
        onChangeText={(t) => { setMiddleName(t); try { setSenderMiddleName?.(t); } catch {} }}
      />

      {/* Last name */}
      <TextInput
        style={styles.input}
        placeholder={lastNamePlaceholder}
        value={lastName}
        onChangeText={(t) => { setLastName(t); try { setSenderLastName?.(t); } catch {} }}
      />

      {/* Hint box */}
      <View style={styles.hintBox}>
        <Image
          source={IdCard}
          style={{height: 24, width: 24}}        
        />
        <Text style={styles.hintText}>
          {t("MAKE_SURE_TO_USE_NAME_ON_YOUR_LEGAL_ID")}
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    paddingHorizontal: 24,
    marginTop: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 4,
    color: "#111827",
  },
  subtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 20,
  },
  input: {
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 16,
    marginBottom: 14,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 10,
    padding: 12,
    marginTop: 10,
    backgroundColor: "#fff",
    marginBottom: 50
  },
  hintText: {
    fontSize: 14,
    color: "#111827",
    marginLeft: 8,
    flex: 1,
  },
});